import React, { useEffect, useState, useRef } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'

import { filtrosArrayBarra } from '../../../../redux/actions'
import { ESimsService } from '../../../../services/http/eSims.service'
import { useGlobal } from '../../../../stores/useGlobal'
import {
  formatArrayToString,
  formatDateToDatePicker,
  formatDateToFilter,
  formatDateToGetFilter,
  formatStringToNumberArray
} from '../../../../utils/genericFormatters'
import DatePicker from '../../../Mantine/DatePicker'
import Grid from '../../../Mantine/Grid'
import Modal from '../../../Mantine/Modal'
import MultiSelect from '../../../Mantine/MultiSelect'

import * as S from './styles'

const REACT_QUERY_OPTIONS = {
  keepPreviousData: true,
  refetchOnWindowFocus: false
}

const WIDTH_COLUMNS = [6, 6, 4, 4, 4, 6]

const DEFAULT_FILTERS_VALUES = {
  carriers: [],
  customers: [],
  offers: [],
  profiles: [],
  statuses: [],
  lastUpdate: {
    start: null,
    end: null
  }
}

export default function FiltroDispositivosEsims({ opened, close }) {
  const { filtros, setFiltros, setRevelarBarraFiltro, setIsLoadingGlobal } =
    useGlobal()
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    customers: [],
    offers: [],
    profiles: [],
    statuses: [],
    lastUpdate: {
      start: null,
      end: null
    }
  })
  const [localFilters, setLocalFilters] = useState({
    ...DEFAULT_FILTERS_VALUES
  })
  const [minHeight, setMinHeight] = useState('')
  const isMobile = useMediaQuery({ maxWidth: 767 })

  const contentRef = useRef(null)

  let arraySimcardFilters = []

  const despacho = useDispatch()

  useEffect(() => {
    // Logica para responsividade: Modal, grid e datepicker com datas na mesma linha
    const handleResize = () => {
      if (contentRef.current) {
        const contentElement = contentRef.current
        const contentRect = contentElement.getBoundingClientRect()

        setMinHeight(
          window.innerWidth <= 767 ? `${contentRect.height + 235}px` : ''
        )
      }
    }
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [contentRef])

  useEffect(() => {
    if (!filtros.vazio) {
      const auxFilter = {
        ...DEFAULT_FILTERS_VALUES
      }

      if (filtros?.CARRIER) {
        auxFilter.carriers = formatStringToNumberArray(filtros.CARRIER)
      }

      if (filtros?.CUSTOMER) {
        auxFilter.customers = formatStringToNumberArray(filtros.CUSTOMER)
      }

      if (filtros?.OFFER) {
        auxFilter.offers = formatStringToNumberArray(filtros.OFFER)
      }

      if (filtros?.PROFILE) {
        auxFilter.profiles = formatStringToNumberArray(filtros.PROFILE)
      }

      if (filtros?.STATUS) {
        auxFilter.statuses = formatStringToNumberArray(filtros.STATUS)
      }

      if (filtros?.LASTUPDATESTART && filtros?.LASTUPDATEEND) {
        auxFilter.lastUpdate = {
          start: formatDateToDatePicker(filtros?.LASTUPDATESTART),
          end: formatDateToDatePicker(filtros?.LASTUPDATEEND)
        }
      }
      // eslint-disable-next-line no-console
      setLocalFilters(auxFilter)
    }
  }, [filtros])

  const { getESimsFilter } = ESimsService

  const getFilters = async () => {
    try {
      setIsLoadingGlobal(true)
      const filtersData = await getESimsFilter()

      setFilterOptions((prev) => ({
        ...prev,
        carriers: filtersData?.carrier?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        customers: filtersData?.customerName?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        offers: filtersData?.offer?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        profiles: filtersData?.profile?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        statuses: filtersData?.status?.map((item) => ({
          value: item.id,
          label: item.label
        }))
      }))
    } catch (error) {
      console.error('Erro ao buscar filtros de dispositivos eSIMs', error)
    } finally {
      setIsLoadingGlobal(false)
    }
  }

  useQuery(['esims/devices/filters'], async () => await getFilters(), {
    ...REACT_QUERY_OPTIONS
  })

  const handleLocalFiltroChange = (fieldPath, value) => {
    const [parent, child] = fieldPath.split('.')

    setLocalFilters((prev) => {
      if (!child) {
        return {
          ...prev,
          [parent]: value
        }
      }

      return {
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }
    })
  }

  const handleCleanFilters = () => {
    setLocalFilters({ ...DEFAULT_FILTERS_VALUES })
    setFiltros({ vazio: 'vazio' })
    despacho(filtrosArrayBarra([]))
    setRevelarBarraFiltro(false)
  }

  const formattedFilter = () => {
    const addFilters = (localFilterKey, filterOptionKey, prefix = '') => {
      const filteredItems = filterOptions[filterOptionKey].filter((item) =>
        localFilters[localFilterKey].includes(item.value)
      )

      const formattedItems = filteredItems.map(
        (item) => `${prefix}${item.label?.trim()} _${item.value}`
      )

      arraySimcardFilters.push(...formattedItems)
    }

    const addDateRangeFilter = (filterKey, label) => {
      const { start, end } = localFilters[filterKey] || {}
      if (start && end) {
        arraySimcardFilters.push(
          `${label}: ${formatDateToFilter(start)} e ${formatDateToFilter(end)}`
        )
      }
    }

    if (localFilters.carriers.length > 0)
      addFilters('carriers', 'carriers', 'carrierESim: ')
    if (localFilters.customers.length > 0)
      addFilters('customers', 'customers', 'customerESim: ')
    if (localFilters.offers.length > 0)
      addFilters('offers', 'offers', 'offerESim: ')
    if (localFilters.profiles.length > 0)
      addFilters('profiles', 'profiles', 'profileESim: ')
    if (localFilters.statuses.length > 0)
      addFilters('statuses', 'statuses', 'statusESim: ')

    addDateRangeFilter('lastUpdate', 'lastUpdateESim')

    despacho(filtrosArrayBarra(arraySimcardFilters))
  }

  const formatarValor = (value, formatter) => {
    return value && value.length ? formatter(value) : undefined
  }

  const formatarData = (value) => {
    return value ? formatDateToGetFilter(value) : undefined
  }

  const handleFilter = () => {
    const filteredData = {
      CARRIER: formatarValor(localFilters.carriers, formatArrayToString),
      CUSTOMER: formatarValor(localFilters.customers, formatArrayToString),
      OFFER: formatarValor(localFilters.offers, formatArrayToString),
      PROFILE: formatarValor(localFilters.profiles, formatArrayToString),
      STATUS: formatarValor(localFilters.statuses, formatArrayToString),
      LASTUPDATESTART: formatarData(localFilters.lastUpdate.start),
      LASTUPDATEEND: formatarData(localFilters.lastUpdate.end)
    }

    const finalFiltros = Object.fromEntries(
      Object.entries(filteredData).filter(([, value]) => value !== undefined)
    )

    setFiltros(finalFiltros)
    setRevelarBarraFiltro(true)

    formattedFilter()
    close()
  }

  return (
    <Modal
      width="1000px"
      type="filtro"
      titulo="Filtros"
      opened={opened}
      close={close}
      minHeight={minHeight}
      onConfirm={handleFilter}
      handleCleanFilters={handleCleanFilters}
    >
      <S.Container ref={contentRef}>
        <S.Card>
          <Grid responsive colSize={WIDTH_COLUMNS}>
            <MultiSelect
              label="Nome do cliente"
              options={filterOptions.customers}
              value={localFilters.customers}
              onChange={(value) => handleLocalFiltroChange('customers', value)}
            />
            <MultiSelect
              label="Oferta"
              options={filterOptions.offers}
              value={localFilters.offers}
              onChange={(value) => handleLocalFiltroChange('offers', value)}
            />
            <MultiSelect
              label="Operadora"
              options={filterOptions.carriers}
              value={localFilters.carriers}
              onChange={(value) => handleLocalFiltroChange('carriers', value)}
            />
            <MultiSelect
              label="Perfil"
              options={filterOptions.profiles}
              value={localFilters.profiles}
              onChange={(value) => handleLocalFiltroChange('profiles', value)}
            />
            <MultiSelect
              label="Status"
              options={filterOptions.statuses}
              value={localFilters.statuses}
              onChange={(value) => handleLocalFiltroChange('statuses', value)}
            />

            <S.FieldsWrapper mobile={isMobile}>
              <DatePicker
                label="Última atualização"
                prefixo="De:"
                value={localFilters.lastUpdate.start}
                onChange={(date) =>
                  handleLocalFiltroChange('lastUpdate.start', date)
                }
              />
              <DatePicker
                prefixo="Até:"
                minDate={localFilters.lastUpdate.start}
                value={localFilters.lastUpdate.end}
                onChange={(date) =>
                  handleLocalFiltroChange('lastUpdate.end', date)
                }
              />
            </S.FieldsWrapper>
          </Grid>
        </S.Card>
      </S.Container>
    </Modal>
  )
}
